<template>
  <div class="mutual-choice-page">
    <div class="choice-header">
      <button class="back-btn" @click="$router.go(-1)">
        <van-icon name="arrow-left" />
      </button>
      <div class="header-main">
        <div class="header-title">互选大厅</div>
        <div class="header-desc" v-if="activityInfo.mutualChoiceStartTime && activityInfo.mutualChoiceEndTime">
          互选时间：{{ formatTime(activityInfo.mutualChoiceStartTime) }} - {{ formatTime(activityInfo.mutualChoiceEndTime) }}
        </div>
      </div>
    </div>

    <div class="not-in-time" v-if="!isEligibleToEnter">
      <div class="empty-state">
        <div class="empty-icon">⏰</div>
        <div class="empty-text">{{ accessDeniedTitle }}</div>
        <div class="empty-hint">{{ accessDeniedHint }}</div>
      </div>
    </div>

    <div class="member-section" v-else>
      <div class="member-grid">
        <div class="member-card" v-for="item in memberList" :key="item.id" @click="viewDetail(item)">
          <div class="card-image">
            <img :src="item.avatar || defaultPhoto" alt="">
          </div>
          <div class="card-body">
            <div class="card-row">
              <span class="card-code">编号：{{ formatSerial(item.memberNo) || '未分配' }}</span>
              <span class="gender-chip">{{ item.sex === 1 ? '男' : '女' }}</span>
            </div>
            <div class="card-meta">
              <span>{{ item.age }}岁</span>
              <span>{{ item.height || '-' }}cm</span>
              <span>{{ getEducationName(item.education) }}</span>
            </div>
          </div>
          <div class="card-actions">
            <button
              class="like-btn"
              :class="{ disabled: isButtonDisabled(item) }"
              :disabled="isButtonDisabled(item)"
              @click.stop="handleLike(item)"
            >
              {{ getButtonText(item) }}
            </button>
          </div>
        </div>
      </div>

      <van-loading v-if="loading" type="spinner" color="#FF2593" class="loading-more" />

      <div v-if="!loading && memberList.length === 0" class="empty-state">
        <div class="empty-icon">🌸</div>
        <div class="empty-text">暂无其他参与者</div>
      </div>
    </div>

    <van-popup v-model="showFavorites" position="bottom" round class="favorites-popup-shell">
      <div class="favorites-popup">
        <div class="popup-header">
          <span class="popup-title">当前活动已喜欢</span>
          <van-icon name="cross" @click="showFavorites = false" />
        </div>
        <div class="favorites-list" v-if="currentActivityFavorites.length > 0">
          <div
            class="favorite-member-item"
            v-for="item in currentActivityFavorites"
            :key="item.memberId"
            @click="viewDetail(item)"
          >
            <div class="favorite-avatar">
              <img :src="item.avatarUrl || defaultPhoto" alt="">
            </div>
            <div class="favorite-info">
              <div class="favorite-name-row">
                <span class="favorite-name">{{ item.name }}</span>
                <span class="favorite-age">{{ item.age }}岁</span>
              </div>
              <div class="favorite-meta">
                <span>#{{ formatSerial(item.memberNo) || '未分配' }}</span>
                <span>{{ item.sex === 1 ? '男' : '女' }}</span>
                <span>{{ item.height || '-' }}cm</span>
                <span>{{ getEducationName(item.education) }}</span>
              </div>
            </div>
            <button class="cancel-btn" @click.stop="handleCancelLike(item)">取消</button>
          </div>
        </div>
        <div v-else class="empty-state popup-empty">
          <div class="empty-text">当前活动暂未喜欢任何人</div>
        </div>
      </div>
    </van-popup>

    <div class="selected-tip" v-if="hasSelectedCurrentActivity" @click="openFavorites">
      <van-icon name="friends-o" />
      <span>已选择心仪对象，点击查看</span>
    </div>
  </div>
</template>

<script>
import { Toast, Dialog } from "vant";
import { getActivityInfoById, getMutualChoiceMembers, submitMutualChoice, cancelMutualChoice, getMyFavoriteMembers, checkUserSignedUp } from "@/api/activityInfo";
import { mapGetters } from "vuex";
import defaultPhoto from "../assets/defaultPhoto.png";

export default {
  data() {
    return {
      activityId: null,
      activityInfo: {},
      memberList: [],
      loading: false,
      selectedCount: 0,
      showFavorites: false,
      currentActivityFavorites: [],
      selectedMemberId: null,
      approvalStatus: null,
      checkInStatus: 0,
      accessDeniedMessage: "",
      defaultPhoto: defaultPhoto,
      educationData: ["大专", "本科", "硕士", "博士", "高中及以下"]
    };
  },
  computed: {
    ...mapGetters(["baseInfo", "role"]),
    isInMutualChoiceTime() {
      const now = new Date();
      const startTime = this.activityInfo.mutualChoiceStartTime ? new Date(this.activityInfo.mutualChoiceStartTime) : null;
      const endTime = this.activityInfo.mutualChoiceEndTime ? new Date(this.activityInfo.mutualChoiceEndTime) : null;

      if (!startTime || !endTime) {
        return false;
      }
      return now >= startTime && now <= endTime;
    },
    hasSelectedCurrentActivity() {
      return !!this.selectedMemberId;
    },
    isCheckedIn() {
      return this.checkInStatus === 1;
    },
    isEligibleToEnter() {
      return this.isInMutualChoiceTime && this.approvalStatus === 2 && this.isCheckedIn;
    },
    accessDeniedTitle() {
      if (this.accessDeniedMessage) {
        return this.accessDeniedMessage;
      }
      if (!this.isInMutualChoiceTime) {
        return "互选还未开始";
      }
      return "暂不可进入互选大厅";
    },
    accessDeniedHint() {
      if (this.accessDeniedMessage) {
        return "请返回活动详情查看当前活动状态";
      }
      if (this.approvalStatus !== 2) {
        return "报名审核通过后才可进入互选大厅";
      }
      if (!this.isCheckedIn) {
        return "请先完成签到后再进入互选大厅";
      }
      if (!this.isInMutualChoiceTime) {
        return "请耐心等待开放时间";
      }
      return "请稍后再试";
    }
  },
  methods: {
    formatTime(timeStr) {
      if (!timeStr) return "";
      const date = new Date(timeStr);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${month}-${day} ${hours}:${minutes}`;
    },
    isButtonDisabled(item) {
      if (item.isSelf) {
        return true;
      }
      if (item.isLiked) {
        return true;
      }
      return this.hasSelectedCurrentActivity;
    },
    getButtonText(item) {
      if (item.isSelf) {
        return "自己";
      }
      if (item.isLiked) {
        return "已喜欢";
      }
      if (this.hasSelectedCurrentActivity) {
        return "已选择其他人";
      }
      return "喜欢";
    },
    getEducationName(education) {
      if (!education) return "-";
      const index = education - 1;
      return this.educationData[index] || "-";
    },
    formatSerial(serialNumber) {
      if (!serialNumber) return "0000";
      return String(serialNumber).padStart(4, "0");
    },
    viewDetail(item) {
      this.$router.push({ path: `/detailInformation/${item.memberId}` });
    },
    async loadActivityInfo() {
      try {
        const res = await getActivityInfoById(this.activityId);
        if (res.code === 20000 && res.data) {
          this.activityInfo = res.data || {};
        }
      } catch (error) {
        if (error && error._canceled) {
          return;
        }
        this.accessDeniedMessage = "活动信息加载失败";
      }
    },
    async loadAccessState() {
      const memberId = this.baseInfo && this.baseInfo.id;
      if (!memberId) {
        this.approvalStatus = null;
        this.checkInStatus = 0;
        this.accessDeniedMessage = "请先登录后再进入互选大厅";
        return;
      }
      try {
        const res = await checkUserSignedUp(this.activityId, memberId);
        if (res.code === 20000 && res.data) {
          this.approvalStatus = res.data.approvalStatus;
          this.checkInStatus = res.data.checkInStatus;
          this.accessDeniedMessage = "";
        } else {
          this.approvalStatus = null;
          this.checkInStatus = 0;
          this.accessDeniedMessage = "您未报名此活动";
        }
      } catch (error) {
        if (error && error._canceled) {
          return;
        }
        this.approvalStatus = null;
        this.checkInStatus = 0;
        this.accessDeniedMessage = "您未报名此活动";
      }
    },
    loadMembers() {
      if (this.loading) return Promise.resolve();
      this.loading = true;
      const memberId = this.baseInfo && this.baseInfo.id;
      return getMutualChoiceMembers(this.activityId, memberId)
        .then(res => {
          if (res.code === 20000 && res.data) {
            this.activityInfo = res.data.activityInfo || {};
            this.memberList = (res.data.members || []).map(item => ({
              ...item,
              avatar: item.avatarUrl,
              memberId: item.memberId,
              memberNo: item.serialNumber,
              isSelf: item.memberId === memberId,
              isLiked: item.isSelected || false
            }));
            const selectedMember = this.memberList.find(item => item.isLiked);
            this.selectedMemberId = selectedMember ? selectedMember.memberId : null;
            this.selectedCount = this.selectedMemberId ? 1 : 0;
          }
        })
        .catch(error => {
          if (error && error._canceled) {
            return;
          }
          this.memberList = [];
          this.selectedMemberId = null;
          this.selectedCount = 0;
          if (error && error.message) {
            this.accessDeniedMessage = error.message;
          } else if (this.isInMutualChoiceTime && this.approvalStatus === 2 && !this.isCheckedIn) {
            this.accessDeniedMessage = "请先签到后再进入互选大厅";
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleLike(item) {
      if (item.isSelf) {
        Toast.fail("不能选择自己");
        return;
      }
      if (item.isLiked) {
        Toast.info("已经选择过该用户");
        return;
      }
      if (this.hasSelectedCurrentActivity) {
        Toast.info("当前活动只能喜欢一位会员");
        return;
      }
      Dialog.confirm({
        message: `确定喜欢编号 ${this.formatSerial(item.memberNo) || ""} 的用户吗？`
      })
        .then(() => {
          submitMutualChoice({
            activityId: this.activityId,
            chooserMemberId: this.baseInfo.id,
            selectedMemberId: item.memberId
          }).then(async res => {
            if (res.code === 20000) {
              Toast.success("互选成功");
              await this.loadMembers();
              if (this.showFavorites) {
                this.loadCurrentActivityFavorites();
              }
            } else {
              Toast.fail(res.message || "互选失败");
            }
          });
        })
        .catch(() => {});
    },
    loadCurrentActivityFavorites() {
      const memberId = this.baseInfo && this.baseInfo.id;
      if (!memberId) return;
      getMyFavoriteMembers(this.activityId, memberId).then(res => {
        if (res.code === 20000 && res.data) {
          this.currentActivityFavorites = (res.data || []).map(item => ({
            ...item,
            memberId: item.memberId,
            memberNo: item.serialNumber
          }));
        }
      });
    },
    openFavorites() {
      this.showFavorites = true;
      this.loadCurrentActivityFavorites();
    },
    handleCancelLike(item) {
      Dialog.confirm({
        message: `确定取消喜欢编号 ${this.formatSerial(item.memberNo) || ""} 的用户吗？`
      })
        .then(() => {
          cancelMutualChoice({
            activityId: this.activityId,
            chooserMemberId: this.baseInfo.id,
            canceledMemberId: item.memberId
          }).then(async res => {
            if (res.code === 20000) {
              Toast.success("已取消喜欢");
              await this.loadMembers();
              this.loadCurrentActivityFavorites();
            } else {
              Toast.fail(res.message || "取消失败");
            }
          });
        })
        .catch(() => {});
    }
  },
  async created() {
    this.activityId = this.$route.params.activityId;
    await this.loadActivityInfo();
    await this.loadAccessState();
    if (this.isEligibleToEnter) {
      await this.loadMembers();
    }
  }
};
</script>

<style lang="less" scoped src="../style/mutualChoice.less" />



// WEBPACK FOOTER //
// src/views/mutualChoice.vue